import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { BehaviorSubject, Subscription, catchError, map, of, switchMap, timer } from 'rxjs';
import { ProductResponse } from '../../../product-list/models/product-interface';
import { PopularProductApiService } from '../../services/popularproduct-api.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorIntlService } from 'src/app/shared/i18n/paginator-intl';

function createDesignPaginatorIntl(translate: TranslateService): MatPaginatorIntl {
  const intl = new PaginatorIntlService(translate);
  intl.getRangeLabel = (page, pageSize, length) => translate.instant('home.design.page_range', {
    start: length === 0 || pageSize === 0 ? 0 : page * pageSize + 1,
    end: Math.min((page + 1) * pageSize, length),
    total: length,
  });
  return intl;
}

export interface MoqFilterOption {
  value: number | null;
  labelKey: string;
  badgeKey?: string;
  descKey: string;
}

@Component({
  selector: 'app-home-designproducts',
  templateUrl: './home-designproducts.component.html',
  styleUrls: ['./home-designproducts.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatPaginatorIntl, useFactory: createDesignPaginatorIntl, deps: [TranslateService] }],
})
export class HomeDesignproductsComponent implements OnDestroy {
  @Input() wishlistIds: string[] = [];
  @Input() curated = false;
  @Output() wishlistChanged = new EventEmitter<void>();
  products: ProductResponse[] = [];
  loading = true;
  error = false;
  minQuantity: number | null = 1;
  isOpen = false;
  pageIndex = 0;
  totalCount = 0;
  search = '';

  readonly moqOptions: MoqFilterOption[] = [
    { value: null, labelKey: 'home.design.all', descKey: 'home.design.moq_all_desc' },
    { value: 1, labelKey: 'home.design.moq_1_label', badgeKey: 'home.design.moq_1_badge', descKey: 'home.design.moq_1_desc' },
    { value: 2, labelKey: 'home.design.moq_2_label', badgeKey: 'home.design.moq_bulk_badge', descKey: 'home.design.moq_2_desc' },
    { value: 3, labelKey: 'home.design.moq_3_label', badgeKey: 'home.design.moq_bulk_badge', descKey: 'home.design.moq_3_desc' },
    { value: 4, labelKey: 'home.design.moq_4_label', badgeKey: 'home.design.moq_bulk_badge', descKey: 'home.design.moq_4_desc' },
    { value: 5, labelKey: 'home.design.moq_5_label', badgeKey: 'home.design.moq_bulk_badge', descKey: 'home.design.moq_5_desc' },
    { value: 6, labelKey: 'home.design.moq_6_label', badgeKey: 'home.design.moq_bulk_badge', descKey: 'home.design.moq_6_desc' },
  ];

  private readonly filter$ = new BehaviorSubject<{ minQuantity: number | null; pageIndex: number; search: string; debounce: boolean }>({ minQuantity: 1, pageIndex: 0, search: '', debounce: false });
  private subscription?: Subscription;

  constructor(
    private api: PopularProductApiService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  get selectedOption(): MoqFilterOption {
    return this.moqOptions.find(opt => opt.value === this.minQuantity) ?? this.moqOptions[0];
  }

  loadProducts(): void {
    if (this.subscription) return;
    this.subscription = this.filter$.pipe(
      switchMap(({ minQuantity, pageIndex, search, debounce }) => {
        this.loading = true;
        this.error = false;
        this.products = [];
        this.cdr.markForCheck();
        return (debounce ? timer(400) : of(0)).pipe(
          switchMap(() => this.curated
            ? this.api.fetchCuratedDesignProducts()
            : this.api.fetchDesignProducts(minQuantity, pageIndex, search)),
          map(response => ({ products: response.data.items, totalCount: response.data.totalCount, pageIndex: response.data.pageIndex, error: false })),
          catchError(() => of({ products: [] as ProductResponse[], totalCount: this.totalCount, pageIndex, error: true }))
        );
      })
    ).subscribe(result => {
      this.products = result.products;
      this.totalCount = result.totalCount;
      this.pageIndex = result.pageIndex;
      this.error = result.error;
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  toggleDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = !this.isOpen;
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  selectOption(option: MoqFilterOption): void {
    if (this.minQuantity !== option.value) {
      this.minQuantity = option.value;
      this.pageIndex = 0;
      this.totalCount = 0;
      this.requestPage();
    }
    this.closeDropdown();
  }

  changeFilter(value: string): void {
    this.minQuantity = value === '' ? null : Number(value);
    this.pageIndex = 0;
    this.totalCount = 0;
    this.requestPage();
    this.cdr.markForCheck();
  }

  retry(): void {
    this.requestPage();
  }

  changeSearch(value: string): void {
    const previous = this.search.trim();
    this.search = value;
    if (value.trim() === previous) return;
    this.pageIndex = 0;
    this.totalCount = 0;
    this.requestPage(true);
  }

  changePage(pageIndex: number): void {
    if (this.loading || pageIndex === this.pageIndex) return;
    this.pageIndex = pageIndex;
    this.requestPage();
    this.elementRef.nativeElement.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  private requestPage(debounce = false): void {
    this.filter$.next({ minQuantity: this.minQuantity, pageIndex: this.pageIndex, search: this.search.trim(), debounce });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.filter$.complete();
  }
}
